import { ODOO_BASE_URL } from '../../odooConfig';

const isLocalDevelopment =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

const BASE_URL = isLocalDevelopment ? '' : ODOO_BASE_URL;

const API_PREFIX = '/api/admissions';

export class AdmissionsAPI {
  constructor(baseURL = BASE_URL, defaultParams = {}) {
    this.baseURL = baseURL;
    this.defaultParams = defaultParams;
    this.onUnauthorized = null;
  }

  setUnauthorizedHandler(handler) {
    this.onUnauthorized = handler;
  }

  async request(endpoint, params = {}, token = null, options = {}) {
    const { suppressUnauthorized = false } = options;
    const headers = { 'Content-Type': 'application/json' };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        jsonrpc: '2.0',
        method: 'call',
        params: { ...this.defaultParams, ...params },
        id: Date.now(),
      }),
    });

    if (!suppressUnauthorized && response.status === 401 && this.onUnauthorized) {
      this.onUnauthorized();
    }

    if (!response.ok) {
      const errorText = await response.text();
      const err = new Error(
        response.status === 404
          ? `Endpoint not found (404): ${endpoint}`
          : errorText || response.statusText
      );
      err.httpStatus = response.status;
      err.code = response.status;
      if (!suppressUnauthorized && response.status === 401 && this.onUnauthorized) {
        this.onUnauthorized();
      }
      throw err;
    }

    const data = await response.json();

    if (data.error) {
      const nested =
        data.error.data?.message ||
        data.error.data?.debug ||
        data.error.message ||
        'API request failed';
      const message =
        String(nested)
          .replace(/^Odoo Server Error:?\s*/i, '')
          .split('\n')[0]
          .trim() || 'API request failed';
      const err = new Error(message);
      err.errorData = data.error;
      err.code = data.error.code;
      throw err;
    }

    const result = data.result ?? data;

    if (result?.success === false) {
      const code = result.code ?? 400;
      if (!suppressUnauthorized && (code === 401 || result.error === 'Unauthorized')) {
        if (this.onUnauthorized) this.onUnauthorized();
      }
      const message =
        result.message || result.error || data.error?.message || 'API request failed';
      const err = new Error(message);
      err.code = code;
      err.result = result;
      throw err;
    }

    return result;
  }

  ping() {
    return this.request(`${API_PREFIX}/ping`);
  }

  me(token, options = {}) {
    return this.request(`${API_PREFIX}/me`, {}, token, options);
  }

  getStudents(params, token) {
    return this.request(`${API_PREFIX}/students`, params, token, {
      suppressUnauthorized: true,
    });
  }

  getStudent(params, token) {
    return this.request(`${API_PREFIX}/students/get`, params, token, {
      suppressUnauthorized: true,
    });
  }

  createStudent(params, token) {
    return this.request(`${API_PREFIX}/students/create`, params, token, {
      suppressUnauthorized: true,
    });
  }

  updateStudent(params, token) {
    return this.request(`${API_PREFIX}/students/update`, params, token, {
      suppressUnauthorized: true,
    });
  }

  async admitStudent(studentId, token) {
    const params = { student_id: Number(studentId) };
    try {
      return await this.request(`${API_PREFIX}/students/admit`, params, token, {
        suppressUnauthorized: true,
      });
    } catch (err) {
      const missing = /not found \(404\)|Endpoint not found/i.test(String(err.message || ''));
      if (!missing) throw err;
      return this.request(
        `${API_PREFIX}/students/update`,
        { ...params, action: 'admit' },
        token,
        { suppressUnauthorized: true }
      );
    }
  }

  archiveStudent(studentId, active, token) {
    return this.request(
      `${API_PREFIX}/students/archive`,
      { student_id: Number(studentId), active },
      token,
      { suppressUnauthorized: true }
    );
  }

  async archiveStudents(studentIds, active, token) {
    const ids = [
      ...new Set(
        (studentIds || [])
          .map(Number)
          .filter((id) => Number.isFinite(id) && id > 0)
      ),
    ];
    if (!ids.length) {
      throw new Error('No students selected.');
    }
    try {
      return await this.request(
        `${API_PREFIX}/students/archive`,
        { student_ids: ids, ids, active },
        token,
        { suppressUnauthorized: true }
      );
    } catch (err) {
      const msg = String(err.message || '');
      const bulkUnsupported = /student_id is required/i.test(msg);
      if (!bulkUnsupported) throw err;
    }
    let last;
    for (const studentId of ids) {
      last = await this.archiveStudent(studentId, active, token);
    }
    return { ...(last || {}), updated: ids.length };
  }

  duplicateStudent(studentId, token) {
    return this.request(
      `${API_PREFIX}/students/update`,
      { student_id: Number(studentId), action: 'duplicate' },
      token,
      { suppressUnauthorized: true }
    );
  }

  deleteStudent(studentId, token) {
    return this.request(
      `${API_PREFIX}/students/update`,
      { student_id: Number(studentId), action: 'unlink' },
      token,
      { suppressUnauthorized: true }
    );
  }

  studentAction(studentId, action, token) {
    return this.request(
      `${API_PREFIX}/students/update`,
      { student_id: Number(studentId), action },
      token,
      { suppressUnauthorized: true }
    );
  }

  printStudentIdCard(studentId, token) {
    return this.request(
      `${API_PREFIX}/students/print-id-card`,
      { student_id: Number(studentId) },
      token,
      { suppressUnauthorized: true }
    );
  }

  printAdmissionLetter(studentId, token) {
    return this.request(
      `${API_PREFIX}/students/print-admission-letter`,
      { student_id: Number(studentId) },
      token,
      { suppressUnauthorized: true }
    );
  }

  getStudentTransfer(studentId, token) {
    return this.request(
      `${API_PREFIX}/students/transfer/get`,
      { student_id: Number(studentId) },
      token,
      { suppressUnauthorized: true }
    );
  }

  previewStudentTransfer(params, token) {
    return this.request(`${API_PREFIX}/students/transfer/preview`, params, token, {
      suppressUnauthorized: true,
    });
  }

  confirmStudentTransfer(params, token) {
    return this.request(`${API_PREFIX}/students/transfer/confirm`, params, token, {
      suppressUnauthorized: true,
    });
  }

  getStudentExternalTransfer(studentId, token) {
    return this.request(
      `${API_PREFIX}/students/external-transfer/get`,
      { student_id: Number(studentId) },
      token,
      { suppressUnauthorized: true }
    );
  }

  previewStudentExternalTransfer(params, token) {
    return this.request(`${API_PREFIX}/students/external-transfer/preview`, params, token, {
      suppressUnauthorized: true,
    });
  }

  confirmStudentExternalTransfer(params, token) {
    return this.request(`${API_PREFIX}/students/external-transfer/confirm`, params, token, {
      suppressUnauthorized: true,
    });
  }

  getCampuses(token) {
    return this.request(`${API_PREFIX}/lookups/campuses`, {}, token, {
      suppressUnauthorized: true,
    });
  }

  getFaculties(token, extra = {}) {
    const params = {};
    if (extra.exclude_pg) params.exclude_pg = true;
    return this.request(`${API_PREFIX}/lookups/faculties`, params, token, {
      suppressUnauthorized: true,
    });
  }

  getDepartments(facultyId, token, extra = {}) {
    const params = {};
    if (facultyId) params.faculty_id = Number(facultyId);
    if (extra.exclude_pg) params.exclude_pg = true;
    return this.request(`${API_PREFIX}/lookups/departments`, params, token, {
      suppressUnauthorized: true,
    });
  }

  getBatches(departmentId, token, extra = {}) {
    const params = {};
    if (departmentId) params.department_id = Number(departmentId);
    if (extra.faculty_id) params.faculty_id = Number(extra.faculty_id);
    if (extra.exclude_pg) params.exclude_pg = true;
    return this.request(`${API_PREFIX}/lookups/batches`, params, token, {
      suppressUnauthorized: true,
    });
  }

  getIntakes(token) {
    return this.request(`${API_PREFIX}/lookups/intakes`, {}, token, {
      suppressUnauthorized: true,
    });
  }

  getAcademicYears(token) {
    return this.request(`${API_PREFIX}/lookups/academic-years`, {}, token, {
      suppressUnauthorized: true,
    });
  }

  getSchools(search, token, extra = {}) {
    const params = { limit: 500, ...extra };
    if (search) params.search = search;
    return this.request(`${API_PREFIX}/lookups/schools`, params, token, {
      suppressUnauthorized: true,
    });
  }

  createSchool(name, token) {
    return this.request(API_PREFIX + '/lookups/schools/create', { name }, token, {
      suppressUnauthorized: true,
    });
  }

  getNationalities(search, token, extra = {}) {
    const params = { limit: 500, ...extra };
    if (search) params.search = search;
    return this.request(`${API_PREFIX}/lookups/nationalities`, params, token, {
      suppressUnauthorized: true,
    });
  }

  getCategories(token) {
    return this.request(`${API_PREFIX}/lookups/categories`, {}, token, {
      suppressUnauthorized: true,
    });
  }

  // ---- Prospective Students ----

  getProspectiveStudents(params, token) {
    return this.request(`${API_PREFIX}/prospective-students`, params, token, {
      suppressUnauthorized: true,
    });
  }

  getProspectiveStudent(params, token) {
    return this.request(`${API_PREFIX}/prospective-students/get`, params, token, {
      suppressUnauthorized: true,
    });
  }

  createProspectiveStudent(params, token) {
    return this.request(`${API_PREFIX}/prospective-students/create`, params, token, {
      suppressUnauthorized: true,
    });
  }

  updateProspectiveStudent(params, token) {
    return this.request(`${API_PREFIX}/prospective-students/update`, params, token, {
      suppressUnauthorized: true,
    });
  }

  archiveProspectiveStudent(prospectiveId, active, token) {
    return this.request(
      `${API_PREFIX}/prospective-students/archive`,
      { prospective_id: Number(prospectiveId), active },
      token,
      { suppressUnauthorized: true }
    );
  }

  duplicateProspectiveStudent(prospectiveId, token) {
    return this.request(
      `${API_PREFIX}/prospective-students/update`,
      { prospective_id: Number(prospectiveId), action: 'duplicate' },
      token,
      { suppressUnauthorized: true }
    );
  }

  deleteProspectiveStudent(prospectiveId, token) {
    // Use /update (already live) — some environments 404 /delete and /unlink paths.
    return this.request(
      `${API_PREFIX}/prospective-students/update`,
      { prospective_id: Number(prospectiveId), action: 'unlink' },
      token,
      { suppressUnauthorized: true }
    );
  }

  getDistricts(token) {
    return this.request(`${API_PREFIX}/lookups/districts`, {}, token, {
      suppressUnauthorized: true,
    });
  }

  getUgStudentListReport(params, token) {
    return this.request(`${API_PREFIX}/reports/ug-student-list`, params, token, {
      suppressUnauthorized: true,
    });
  }

  downloadUgStudentListPdf(params, token) {
    return this.request(`${API_PREFIX}/reports/ug-student-list/pdf`, params, token, {
      suppressUnauthorized: true,
    });
  }

  downloadUgStudentListExcel(params, token) {
    return this.request(`${API_PREFIX}/reports/ug-student-list/excel`, params, token, {
      suppressUnauthorized: true,
    });
  }

  getVisitPurposes(token) {
    return this.request(`${API_PREFIX}/lookups/visit-purposes`, {}, token, {
      suppressUnauthorized: true,
    });
  }

  getInfoSources(token) {
    return this.request(`${API_PREFIX}/lookups/info-sources`, {}, token, {
      suppressUnauthorized: true,
    });
  }
}

const admissionsAPI = new AdmissionsAPI();
export default admissionsAPI;
